import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackCTA, trackEvent } from "@/lib/tracking";
import { trackPlacementCTA, type PlacementCTA } from "@/lib/cta-placement";
import {
  STICKY_CTA_SLOTS,
  type StickyCTASlot,
  getStickyCTAConfig,
  getStickyCTAConfigVersion,
  subscribeStickyCTAConfig,
} from "./sticky-cta-config";

export { useStickyCTAConfig } from "./sticky-cta-config";
export type { StickyCTAConfig } from "./sticky-cta-config";

/**
 * Analytics event catalog fired by this bar (all routed through
 * `trackEvent`, so they land in `dataLayer` + GA4 automatically):
 *
 *   sticky_cta_impression   — first time the bar becomes visible on this
 *                             route. Payload includes `impression_id`
 *                             (uuid), `path`, `viewport_w/h`, and the
 *                             `ctas` array so each button can be joined
 *                             back to its click event.
 *   sticky_cta_click        — per-button click. Payload includes
 *                             `cta_id`, `cta_kind`
 *                             ('registration' | 'strategy_session'),
 *                             `href`, `impression_id`,
 *                             `time_to_click_ms`, and `position` (0-based
 *                             slot in the bar). Also mirrored via
 *                             `trackCTA` so existing CTA dashboards keep
 *                             working.
 *   sticky_cta_dismissed    — bar was shown at least once and the user
 *                             left the route without clicking. Lets you
 *                             compute a true CTR = clicks / impressions
 *                             and a dismissal rate per page.
 */
const newImpressionId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `imp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Mobile-only sticky conversion bar.
 *
 * Visibility rules (tuned to avoid overlap with primary content/CTAs):
 *   1. Only appears after the user has scrolled past ~40% of the first
 *      viewport height AND at least 480px — proportional threshold works
 *      on short (mobile) and tall (foldable) screens alike.
 *   2. Hides within 320px of the document bottom so it can't cover the
 *      footer CTAs, legal links, or a page's primary submit button.
 *   3. Hides when the on-screen keyboard is open (input focused) so it
 *      doesn't cover the field the user is typing into.
 *   4. Hides when any element with `[data-sticky-cta="suppress"]` is in
 *      the viewport — lets individual pages opt-out around their own
 *      pinned CTAs (pricing tables, checkout, etc.).
 *   5. Hides while scrolling upward (less intrusive).
 *   6. Skipped entirely on form-first pages (/inquire, /scan, /free,
 *      /links, /auth, /book) where a native submit button owns the
 *      bottom edge.
 *   7. Resets on route change so it never flashes mid-navigation.
 *
 * Also exposes `--sticky-cta-height` on <body> while visible so page
 * layouts can add `padding-bottom: var(--sticky-cta-height, 0)` to
 * prevent overlap with their own fixed elements.
 */
export const StickyCTABar = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const wasShownRef = useRef(false);
  // Tracks the element that had focus before the user tabbed *into* the
  // sticky bar, so we can restore focus there if the bar hides (Escape,
  // scroll away, route change) while one of its buttons is focused.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const { pathname } = useLocation();

  const impressionIdRef = useRef<string>("");
  const impressionAtRef = useRef<number>(0);
  const clickedRef = useRef<boolean>(false);

  // Subscribe to per-route config (static map + runtime `useStickyCTAConfig`
  // overrides). `useSyncExternalStore` re-renders the bar whenever a
  // page publishes/clears an override so visibility rules update live.
  useSyncExternalStore(
    subscribeStickyCTAConfig,
    getStickyCTAConfigVersion,
    getStickyCTAConfigVersion,
  );
  const config = getStickyCTAConfig(pathname);
  const isSuppressedPath = config.disabled;
  const slots: StickyCTASlot[] = config.slots
    .map((id) => STICKY_CTA_SLOTS[id])
    .filter(Boolean);

  useEffect(() => {
    setShow(false);
    setDismissed(false);
    wasShownRef.current = false;
    impressionIdRef.current = "";
    impressionAtRef.current = 0;
    clickedRef.current = false;
  }, [pathname]);

  useEffect(() => {
    if (isSuppressedPath || dismissed) return;

    let lastY = window.scrollY;
    let raf = 0;
    let keyboardOpen = false;

    // Hero gating: while any [data-hero-section] is even partially in the
    // viewport the bar stays hidden; it appears once the hero has fully
    // scrolled out and hides again the moment it re-enters.
    let heroEls: HTMLElement[] = [];
    const refreshHeroEls = () => {
      heroEls = Array.from(
        document.querySelectorAll<HTMLElement>("[data-hero-section]"),
      );
    };
    refreshHeroEls();
    let hasHero = heroEls.length > 0;
    const heroVisible = () => {
      // Heroes can mount after this effect (lazy routes); re-query until
      // one is found so gating engages as soon as it exists.
      if (!hasHero) {
        refreshHeroEls();
        hasHero = heroEls.length > 0;
      }
      const vh = window.innerHeight;
      return heroEls.some((el) => {
        const r = el.getBoundingClientRect();
        return r.bottom > 0 && r.top < vh;
      });
    };

    const isTypingTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };

    const anySuppressorVisible = () => {
      const nodes = document.querySelectorAll<HTMLElement>(
        '[data-sticky-cta="suppress"]',
      );
      const vh = window.innerHeight;
      for (const node of nodes) {
        const rect = node.getBoundingClientRect();
        if (rect.top < vh && rect.bottom > vh * 0.7) return true;
      }
      return false;
    };

    const evaluate = () => {
      const y = window.scrollY;
      const viewport = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = docHeight - (y + viewport);

      // All thresholds now come from the resolved per-route config so a
      // page can tune when the bar shows without editing this file.
      const threshold = Math.max(
        config.thresholdPx,
        viewport * config.thresholdVh,
      );
      const pastThreshold = y > threshold;
      const nearFooter = distanceFromBottom < config.nearFooterPx;
      const scrollingDown = y > lastY + 2;
      const scrollingUp = y < lastY - 4;
      const persistentDepth = y > viewport * config.persistAfterVh;
      const heroOnScreen = heroVisible();

      const depthOk = hasHero
        ? !heroOnScreen
        : pastThreshold &&
          (scrollingDown ||
            (!(config.hideOnScrollUp && scrollingUp) && persistentDepth));

      const next = config.alwaysVisible
        ? !keyboardOpen && !anySuppressorVisible()
        : depthOk &&
          !heroOnScreen &&
          !nearFooter &&
          !keyboardOpen &&
          !anySuppressorVisible();

      setShow(next);
      lastY = y;
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(evaluate);
    };

    const onFocusIn = (e: FocusEvent) => {
      if (isTypingTarget(e.target)) {
        keyboardOpen = true;
        setShow(false);
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      if (isTypingTarget(e.target)) {
        keyboardOpen = false;
        requestAnimationFrame(evaluate);
      }
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [
    isSuppressedPath,
    pathname,
    dismissed,
    config.thresholdPx,
    config.thresholdVh,
    config.nearFooterPx,
    config.persistAfterVh,
    config.hideOnScrollUp,
    config.alwaysVisible,
  ]);

  useEffect(() => {
    const body = document.body;
    if (show && barRef.current) {
      const measured = barRef.current.getBoundingClientRect().height;
      const h = Math.max(measured, config.minHeightPx ?? 0);
      body.style.setProperty("--sticky-cta-height", `${Math.round(h)}px`);
    } else {
      body.style.setProperty("--sticky-cta-height", "0px");
    }
    return () => {
      body.style.setProperty("--sticky-cta-height", "0px");
    };
  }, [show, config.minHeightPx]);

  useEffect(() => {
    if (!show || !barRef.current || impressionIdRef.current) return;
    const el = barRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            impressionIdRef.current = newImpressionId();
            impressionAtRef.current = Date.now();
            wasShownRef.current = true;
            trackEvent("sticky_cta_impression", {
              impression_id: impressionIdRef.current,
              path: pathname,
              viewport_w: window.innerWidth,
              viewport_h: window.innerHeight,
              ctas: slots.map((c) => `${c.id}:${c.kind}`).join(","),
              cta_count: slots.length,
              surface: "sticky_mobile",
      placement: "sticky" as const,
            });
            io.disconnect();
            break;
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, pathname, slots]);

  useEffect(() => {
    return () => {
      if (impressionIdRef.current && !clickedRef.current) {
        trackEvent("sticky_cta_dismissed", {
          impression_id: impressionIdRef.current,
          path: pathname,
          dwell_ms: Date.now() - impressionAtRef.current,
          surface: "sticky_mobile",
        });
      }
    };
  }, [pathname]);

  // Focus management: when the bar hides while one of its buttons has
  // focus (e.g. user pressed Escape, scrolled away, or triggered a
  // suppressor), move focus back to whatever had focus before the user
  // entered the bar — never leave focus stranded on an `inert`/hidden
  // element, which strands keyboard + screen-reader users.
  useEffect(() => {
    if (show || !barRef.current) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && barRef.current.contains(active)) {
      const target = returnFocusRef.current;
      if (target && document.contains(target)) {
        target.focus();
      } else {
        active.blur();
      }
    }
  }, [show]);

  const handleClick = (slot: StickyCTASlot, position: number) => {
    clickedRef.current = true;
    const payload = {
      cta_id: slot.id,
      cta_kind: slot.kind,
      href: slot.href,
      position,
      surface: "sticky_mobile",
      path: pathname,
      impression_id: impressionIdRef.current || "no_impression_recorded",
      time_to_click_ms: impressionAtRef.current
        ? Date.now() - impressionAtRef.current
        : -1,
    };
    trackEvent("sticky_cta_click", payload);
    trackPlacementCTA("sticky", {
      id: slot.id,
      kind: slot.kind as PlacementCTA["kind"],
      label: slot.label,
      href: slot.href,
    }, { position, impression_id: payload.impression_id, time_to_click_ms: payload.time_to_click_ms });
  };

  const handleFocusCapture = (e: React.FocusEvent<HTMLDivElement>) => {
    // Record the previously focused element the first time focus enters
    // the bar so Escape / auto-hide can restore it.
    const prev = e.relatedTarget as HTMLElement | null;
    if (prev && !barRef.current?.contains(prev)) {
      returnFocusRef.current = prev;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // Escape unmounts the bar outright, so the `show`-driven restore
      // effect below never gets a chance to run (its ref is already gone
      // by the time it fires). Move focus back here, synchronously,
      // before React tears the subtree down — otherwise focus falls to
      // <body> and a keyboard user loses their place in the page.
      const restoreTo = returnFocusRef.current;
      if (restoreTo && document.contains(restoreTo)) {
        restoreTo.focus();
      } else {
        (document.activeElement as HTMLElement | null)?.blur();
      }
      // Dismiss for the rest of this route; scroll won't bring it back.
      setDismissed(true);
      setShow(false);
      if (impressionIdRef.current && !clickedRef.current) {
        trackEvent("sticky_cta_dismissed", {
          impression_id: impressionIdRef.current,
          path: pathname,
          dwell_ms: Date.now() - impressionAtRef.current,
          surface: "sticky_mobile",
          reason: "escape_key",
        });
        // Prevent the route-change effect from double-firing dismissal.
        clickedRef.current = true;
      }
    }
  };

  if (isSuppressedPath || dismissed) return null;

  return (
    <nav
      ref={barRef}
      aria-label="Quick actions"
      aria-hidden={!show || undefined}
      // `inert` removes the whole subtree from the tab order and the
      // accessibility tree while the bar is slid off-screen, so users
      // can't tab into invisible buttons. React 19 forwards the boolean;
      // we spread as an attribute to keep older TS lib.dom happy.
      {...(!show ? { inert: "" as unknown as boolean } : {})}
      onFocusCapture={handleFocusCapture}
      onKeyDown={handleKeyDown}
      className={cn(
        "fixed bottom-0 inset-x-0 z-30 md:hidden transition-all duration-300 ease-out",
        // Solid navy background extends into the safe-area inset so the
        // home indicator / gesture bar never sits on top of translucent
        // content bleeding through. The buttons themselves live above
        // the inset via padding-bottom below.
        "bg-navy/95 backdrop-blur-md",
        show
          ? "translate-y-0 opacity-100"
          : "translate-y-full opacity-0 pointer-events-none",
      )}
      style={{
        // `max()` keeps a 4px floor on browsers/devices that report a
        // zero inset (Android, desktop devtools) so the buttons never
        // touch the very bottom edge where browser UI (URL bar, gesture
        // hint) can overlap.
        paddingBottom: "max(env(safe-area-inset-bottom), 0.25rem)",
      }}
    >
      <h2 className="sr-only">Quick actions</h2>
      <p className="sr-only" id="sticky-cta-hint">
        Press Escape to dismiss this action bar.
      </p>
      <ul
        className={cn(
          "grid border-t border-gold/40 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.6)]",
          slots.length > 1 ? "grid-cols-2" : "grid-cols-1",
        )}
        aria-describedby="sticky-cta-hint"
      >
        {slots.map((slot, i) => {
          const isPrimary =
            slot.kind === "strategy_session" ||
            slot.kind === "masterclass" ||
            slots.length === 1;
          const describedAction = slot.action;
          return (
            <li key={slot.id} className="contents">
              <Link
                to={slot.href}
                data-cta-id={slot.id}
                data-cta-kind={slot.kind}
                aria-label={describedAction}
                tabIndex={show ? 0 : -1}
                onClick={() => handleClick(slot, i)}
                className={cn(
                  "flex items-center justify-center gap-1.5 py-3.5 min-h-11 text-[10px] uppercase tracking-[0.2em] text-white touch-manipulation transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy",
                  slots.length === 1 && "text-[11px] tracking-[0.18em] py-4",
                  isPrimary
                    ? "bg-cta shadow-cta active:brightness-110"
                    : "border-r border-border hover:bg-gold/10 active:bg-gold/15",
                )}
              >
                <span aria-hidden="true">{slot.label}</span>
                {isPrimary && <ArrowRight className="h-3 w-3" aria-hidden="true" />}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

